import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import apiConfig from '../../config/apiConfig';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchForeignResource } from '../../apis/resources';
import { fetchEnum } from '../../apis/enum';
import { useParams } from 'react-router-dom';
import { apiFetch } from "../../apis/apiFetch";
import { useRaspStore } from '../../store/raspStore';
import { LoginContext } from '../../context/LoginContext';
import { useCurrentaddressViewModel } from "../../viewModels/useCurrentaddressViewModel";
import './FormStyles.css';
import { validate } from '../../utils/validator';
import { currentAddressValidationRules } from '../../validators/applicantValidator';

export type resourceMetaData = {
  resource: string;
  fieldValues: any[];
};

export interface CurrentaddressFormRef {
  save: () => Promise<Record<string, string>>;
  getErrors: () => Record<string, string>;
  getData: () => { line1: string; line2: string; city: string; state: string; country: string; pincode: string };
}


type CreateApplicantProps = {
  dataToSave: any;
  setDataToSave: React.Dispatch<React.SetStateAction<any>>;
};

// getCookie removed - using apiFetch

const CreateCurrentaddress = forwardRef<CurrentaddressFormRef, CreateApplicantProps>((props, ref) => {
  const { dataToSave, setDataToSave } = props;
  const { user } = useContext(LoginContext); // logged-in user

  const [resMetaData, setResMetaData] = useState<resourceMetaData[]>([]);
  const [showToast, setShowToast] = useState<any>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const regex = /^(g_|archived|extra_data)/;
  const apiUrl = apiConfig.getResourceUrl("applications");
  const metadataUrl = apiConfig.getResourceMetaDataUrl("applications")

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const hasPrePopulated = useRef(false);
  const queryClient = useQueryClient();

  // userId is now managed via LoginContext

  const { appId }: any = useParams<any>();

  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    // dataToSave,
    // setDataToSave,
    loadMetadata,
    save,
  } = useCurrentaddressViewModel(user?.userId || "", appId || "");

  const [countriesList, setCountriesList] = useState<string[]>([]);
  const [statesList, setStatesList] = useState<string[]>([]);

  // Fetch all countries on load
  useEffect(() => {
    fetch("https://countriesnow.space/api/v0.1/countries")
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          const names = data.data.map((item: any) => item.country);
          setCountriesList(names);
        }
      })
      .catch(err => console.error("Error fetching countries:", err));
  }, []);

  // Fetch states when a country is selected
  useEffect(() => {
    if (dataToSave["country"]) {
      fetch("https://countriesnow.space/api/v0.1/countries/states", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country: dataToSave["country"] })
      })
        .then(res => res.json())
        .then(data => {
          if (!data.error) {
            const names = data.data.states.map((item: any) => item.name);
            setStatesList(names);
          } else {
            setStatesList([]);
          }
        })
        .catch(err => console.error("Error fetching states:", err));
    } else {
      setStatesList([]);
    }
  }, [dataToSave["country"]]);

  useEffect(() => {
    loadMetadata();
  }, [])

  // Auto-set user_id from logged-in user's email
  useEffect(() => {
    if (!user || !user.email_id) return;
    const users = foreignKeyData["Users"] || [];
    if (!users.length) return;

    const match = users.find(
      (u: any) => {
        const userEmail = (u.email || u.email_id || u.user_email || "").toLowerCase();
        return userEmail === user.email_id.toLowerCase();
      }
    );

    if (match && match.id) {
      setDataToSave((prev: any) => ({
        ...prev,
        user_id: match.id,
      }));
    } else {
      console.warn("No matching user found for logged-in user email:", user.email_id);
    }
  }, [user, foreignKeyData]);

  // ✅ async function, not useQuery
  const fetchForeignData = async (
    foreignResource: string,
    fieldName: string,
    foreignField: string
  ) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({
        ...prev,
        [foreignResource]: data,
      }));
    } catch (err) {
      console.error(`Error fetching foreign data for ${fieldName}:`, err);
    }
  };

  // ✅ async function, not useQuery
  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({
        ...prev,
        [enumName]: data,
      }));
    } catch (err) {
      console.error(`Error fetching enum data for ${enumName}:`, err);
    }
  };

  const { data: dataRes } = useQuery({
    queryKey: ['resourceData', 'currentaddressCreate', user?.userId, localStorage.getItem("activeDriveId")],
    queryFn: async () => {
      if (!user?.userId) return [];
      const response = await apiFetch(
        `${apiConfig.getResourceUrl('applications')}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      return data.resource || [];
    },
    enabled: !!user?.userId
  });

  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (dataRes && activeDriveId) {
      const activeRecord = dataRes.find((r: any) => r.drive_id === activeDriveId);
      let dataToStore = { resource: 'currentaddress', records: activeRecord ? [activeRecord] : [] }
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
    }
  }, [dataRes, user])

  // Pre-populate form with existing data when user_id is resolved and records are loaded
  useEffect(() => {
    const activeDriveId = localStorage.getItem("activeDriveId");
    if (!hasPrePopulated.current && dataRes && dataRes.length > 0 && dataToSave.user_id && activeDriveId) {
      const userRecord = dataRes.find((r: any) => r.user_id === dataToSave.user_id && r.drive_id === activeDriveId);
      if (userRecord) {
        hasPrePopulated.current = true;
        setDataToSave((prev: any) => ({ ...userRecord, ...prev }));
      }
    }
  }, [dataRes, dataToSave.user_id])

  const handleCreate = async () => {
    const vErrors = validate(dataToSave, currentAddressValidationRules);
    setErrors(vErrors);
    
    await save(dataToSave);
    queryClient.invalidateQueries({ queryKey: ['resourceData', 'currentaddressCreate'] });
    
    return vErrors;
  };

  // Expose save method and getData method to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleCreate,
    getErrors: () => errors,
    getData: () => ({
      line1: dataToSave["line1"] || "",
      line2: dataToSave["line2"] || "",
      city: dataToSave["city"] || "",
      state: dataToSave["state"] || "",
      country: dataToSave["country"] || "",
      pincode: dataToSave["pincode"] || "",
    }),
  }));

  const handleSearchChange = (fieldName: string, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [fieldName]: value }));
  };

  return (
    <div className="form-container">
      <h3 className="form-subtitle">Current Address</h3>

      <div className="form-grid">
        {/* Line 1 */}
        <div className="form-group">
          <label className="form-label">Line 1 *</label>
          <input
            type="text"
            className={`form-input ${errors.line1 ? "input-error" : ""}`}
            name="line1"
            required
            value={dataToSave["line1"] || ""}
            placeholder="Line 1"
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, line1: e.target.value }));
              if (errors.line1) setErrors(prev => ({ ...prev, line1: "" }));
            }}
          />
          {errors.line1 && <span className="error-text">{errors.line1}</span>}
        </div>

        {/* Line 2 */}
        <div className="form-group">
          <label className="form-label">Line 2 *</label>
          <input
            type="text"
            className={`form-input ${errors.line2 ? "input-error" : ""}`}
            name="line2"
            required
            value={dataToSave["line2"] || ""}
            placeholder="Line 2"
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, line2: e.target.value }));
              if (errors.line2) setErrors(prev => ({ ...prev, line2: "" }));
            }}
          />
          {errors.line2 && <span className="error-text">{errors.line2}</span>}
        </div>

        {/* Country */}
        <div className="form-group">
          <label className="form-label">Country *</label>
          <select
            className={`form-select ${errors.country ? "input-error" : ""}`}
            name="country"
            required
            value={dataToSave["country"] || ""}
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, country: e.target.value }));
              if (errors.country) setErrors(prev => ({ ...prev, country: "" }));
            }}
          >
            <option value="">Select Country</option>
            {countriesList.map((val: any, idx: any) => (
              <option key={idx} value={val}>{val}</option>
            ))}
          </select>
          {errors.country && <span className="error-text">{errors.country}</span>}
        </div>

        {/* State */}
        <div className="form-group">
          <label className="form-label">State *</label>
          <select
            className={`form-select ${errors.state ? "input-error" : ""}`}
            name="state"
            required
            value={dataToSave["state"] || ""}
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, state: e.target.value }));
              if (errors.state) setErrors(prev => ({ ...prev, state: "" }));
            }}
          >
            <option value="">Select State</option>
            {statesList.map((val: any, idx: any) => (
              <option key={idx} value={val}>{val}</option>
            ))}
          </select>
          {errors.state && <span className="error-text">{errors.state}</span>}
        </div>

        {/* City */}
        <div className="form-group">
          <label className="form-label">City *</label>
          <input
            type="text"
            className={`form-input ${errors.city ? "input-error" : ""}`}
            name="city"
            required
            value={dataToSave["city"] || ""}
            placeholder="City"
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, city: e.target.value }));
              if (errors.city) setErrors(prev => ({ ...prev, city: "" }));
            }}
          />
          {errors.city && <span className="error-text">{errors.city}</span>}
        </div>

        {/* Pincode */}
        <div className="form-group">
          <label className="form-label">Pincode *</label>
          <input
            type="text"
            className={`form-input ${errors.pincode ? "input-error" : ""}`}
            name="pincode"
            required
            value={dataToSave["pincode"] || ""}
            placeholder="Pincode"
            onChange={(e) => {
              setDataToSave((prev: any) => ({ ...prev, pincode: e.target.value }));
              if (errors.pincode) setErrors(prev => ({ ...prev, pincode: "" }));
            }}
          />
          {errors.pincode && <span className="error-text">{errors.pincode}</span>}
        </div>

        {/* User ID - Auto-populated from logged-in user, hidden from UI */}
        <input type="hidden" name="user_id" value={dataToSave.user_id || ""} />
      </div>
    </div>
  )


});

export default CreateCurrentaddress
